// src/components/NotificationBell.jsx
//
// The topbar notification bell — shared by all three portals.
//
// DEF-027: "Notification bell non-functional across Patient, Navigator, Doctor."
// The bell was a button with a title attribute and no handler. This replaces it
// with a real dropdown backed by useNotifications, and PatientTopbar — which had
// no bell at all — gets the same component rather than its own copy.
//
// TC-N002 relates: the navigator's 2-hour escalation SLA badge had no surface to
// live on ("no Escalations section in sidebar"). Critical escalations now appear
// here, with an OVERDUE state once the SLA is breached.
//
// STYLING
// Uses the existing .topbar-icon-btn class plus the notification-specific rules
// added to src/index.css. Per PREVIU_DESIGN_TOKENS.md: no Tailwind, no component
// library, no hardcoded hex — every colour is a var(--token).

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { formatMarkerText } from '../utils/markerNames';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { useToast } from '../context/ToastContext';
import { notificationTarget } from '../utils/notificationTarget';
import Icon from './Icon';

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor((Date.now() - then) / 60_000);
  if (minutes < 1)    return 'just now';
  if (minutes < 60)   return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)     return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7)       return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/**
 * @param {'PATIENT'|'NAVIGATOR'|'DOCTOR'} role
 * @param {string} [accountRole] the signed-in staff account's own role (HEALTH_NAVIGATOR, ADMIN,
 *        SUPPORT_STAFF…) — several share the NAVIGATOR bell but not the same click targets
 * @param {string} [patientId] required for role PATIENT — care plan is patient-scoped
 * @param {boolean} [canReview] the signed-in account is a DOCTOR or HEALTH_NAVIGATOR (not ADMIN)
 * @param {boolean} [canFollowUp] the signed-in account is a HEALTH_NAVIGATOR (not ADMIN)
 * @param {boolean} [canEscalate] the signed-in account is a DOCTOR, HEALTH_NAVIGATOR or ADMIN (not SUPPORT_STAFF)
 * @param {boolean} [appNotificationsOff] the patient turned in-app notifications
 *        off in Settings. Ignored for every role but PATIENT (see useNotifications).
 */
export default function NotificationBell({ role, accountRole, patientId, plan, canReview, canFollowUp, canEscalate, appNotificationsOff }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('unread'); // 'unread' | 'read'
  const wrapRef = useRef(null);

  const { turnedOff, items, readItems, loading, error, unreadCount, hasCritical, markRead, markAllRead, refresh } =
    useNotifications({ role, patientId, plan, canReview, canFollowUp, canEscalate, appNotificationsOff, enabled: true });

  // Close on outside click and on Escape. Both, not just the click: a dropdown
  // that traps focus with no keyboard way out is unusable with a screen reader.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleToggle = useCallback(() => {
    setOpen(prev => {
      // Refresh on OPEN rather than relying on the poll — the user clicking the
      // bell is the strongest signal they want current data, and the poll
      // interval is deliberately slow (see useNotifications).
      if (!prev) { refresh(); setTab('unread'); }
      return !prev;
    });
  }, [refresh]);

  function handleItemClick(item) {
    // Opening an item is reading it — it leaves the tray and the count drops.
    markRead([item]);
    setOpen(false);
    // Resolved at click time, for live items and Read-tab snapshots alike, so
    // a route that has since moved is never followed (utils/notificationTarget.js).
    const target = notificationTarget(item, role === 'NAVIGATOR' ? (accountRole || role) : role);
    if (target) navigate(target.path);
    else toast.info('This item is no longer available');
  }

  function handleOpenSettings() {
    setOpen(false);
    navigate('/patient/settings#notifications');
  }

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className="topbar-icon-btn notif-btn"
        onClick={handleToggle}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={turnedOff
          ? 'Notifications — turned off'
          : unreadCount > 0
            ? `Notifications — ${unreadCount} unread`
            : 'Notifications — none'}
        title="Notifications"
      >
        <Icon name="bell" size={17} />
        {unreadCount > 0 && (
          <span className={`notif-badge${hasCritical ? ' notif-badge--critical' : ''}`}>
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-panel-header">
            <div>
              <div className="notif-panel-title">Notifications</div>
              <div className="notif-panel-sub">
                {unreadCount > 0 ? `${unreadCount} need your attention` : 'Nothing needs your attention'}
              </div>
            </div>
            {unreadCount > 0 && (
              <button type="button" className="notif-link-btn" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notif-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'unread'}
              className={`notif-tab${tab === 'unread' ? ' active' : ''}`}
              onClick={() => setTab('unread')}>
              Unread <span className="notif-tab-count">{unreadCount}</span>
            </button>
            <button type="button" role="tab" aria-selected={tab === 'read'}
              className={`notif-tab${tab === 'read' ? ' active' : ''}`}
              onClick={() => setTab('read')}>
              Read <span className="notif-tab-count">{readItems.length}</span>
            </button>
          </div>

          <div className="notif-list">
            {turnedOff && (
              <div className="notif-empty">
                In-app notifications are turned off.
                <div>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 10 }}
                    onClick={handleOpenSettings}>
                    Notification settings
                  </button>
                </div>
              </div>
            )}

            {tab === 'unread' && !turnedOff && loading && items.length === 0 && (
              <div className="notif-empty">Loading…</div>
            )}

            {tab === 'unread' && !loading && error && (
              <div className="notif-empty notif-empty--error">{error}</div>
            )}

            {tab === 'unread' && !turnedOff && !loading && !error && items.length === 0 && (
              <div className="notif-empty">
                <div className="notif-empty-icon"><Icon name="check" size={20} /></div>
                You&rsquo;re all caught up
              </div>
            )}

            {tab === 'unread' && items.map(item => (
              <div key={item.id} className={`notif-item notif-item--${item.severity.toLowerCase()}`}>
                <button type="button" className="notif-item-main" onClick={() => handleItemClick(item)}>
                  <span className="notif-item-icon" aria-hidden="true">{item.icon}</span>
                  <span className="notif-item-text">
                    <span className="notif-item-title">{formatMarkerText(item.title)}</span>
                    {item.body && <span className="notif-item-body">{formatMarkerText(item.body)}</span>}
                    <span className="notif-item-time">{relativeTime(item.at)}</span>
                  </span>
                </button>
                <button type="button" className="notif-check-btn" title="Mark as read"
                  aria-label={`Mark "${item.title}" as read`} onClick={() => markRead([item])}>
                  <Icon name="check" size={14} />
                </button>
              </div>
            ))}

            {tab === 'read' && readItems.length === 0 && (
              <div className="notif-empty">No read notifications yet</div>
            )}

            {tab === 'read' && readItems.map(item => (
              <button type="button" key={item.id}
                className={`notif-item notif-item--read notif-item--${(item.severity || 'info').toLowerCase()}`}
                onClick={() => handleItemClick(item)}>
                <span className="notif-item-icon" aria-hidden="true">{item.icon}</span>
                <span className="notif-item-text">
                  <span className="notif-item-title">{formatMarkerText(item.title)}</span>
                  {item.body && <span className="notif-item-body">{formatMarkerText(item.body)}</span>}
                  <span className="notif-item-time">{relativeTime(item.at)}</span>
                </span>
              </button>
            ))}
          </div>

          {tab === 'read' && readItems.length > 0 && (
            <div className="notif-panel-footer">Showing your last 20 read notifications</div>
          )}
        </div>
      )}
    </div>
  );
}
